import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import {
  formDataToFormikMapper,
  getLodgeErrorTypeEnum,
  premiseFormFieldMapper,
} from "src/api/apiHelper";
import { apiClient } from "src/api/apiService";
import axios from "axios";
import {
  Field,
  FieldInputProps,
  FieldProps,
  Form,
  FormikProps,
  useFormikContext,
  yupToFormErrors,
} from "formik";
import FormContext from "src/context/FormContext";
import LicensedElectrician from "./FormComponents/LicencedElectrician";
import MeterProvider from "./FormComponents/MeterProvider";
import MaxLoad from "./FormComponents/MaxLoad";

import FormFieldLayout from "./FormComponents/FormFieldLayout";
import GeneralTextField from "./FormComponents/GeneralTextField";
import FormikSelectField from "./FormComponents/FormikSelectField";
import { AccountPayer, DocumentTypeEnum, FormValues, Service } from "./types";
import { getIsNmiRequired } from "./helpers";

import {
  formValidationSchema,
  homeUserFormValidationSchema,
} from "./FormComponents/Validations";
import { EICStatusEnum, RequestStatusEnum, State } from "../Premises/types";
import { useNavigate } from "react-router-dom";
import UpdatePremiseAlert from "./UpdatePremiseAlert";
import { sendEicComms, showUpdatePremiseAlert } from "./helper";
import { useUpdateSidebarStatus } from "../Hooks/useSidebarStatus";
import HomeUserLicencedElectrician from "./HomeUser/HomeUserLicencedElectrician";
import { useAppContext } from "src/context/AppContext";
import { usePermissions } from "../../hooks/usePermissions";
import { LifeSupportSection } from "./FormComponents/LifeSupportSection";
import PermissionsActionButtons from "./PermissionsActionButtons";
import { NewPremiseFormFileUploadFormikField } from "./NewPremise/NewPremiseFormFileUploadFormikField";

const ElectricityBlock = (props: {
  setFormLoadValues: React.Dispatch<React.SetStateAction<FormValues>>;
  homeUser: boolean;
  premiseId: string | null;
}) => {
  const [isLoading, setIsloading] = useState(true);
  const {
    context: { user },
  } = useAppContext();
  const navigate = useNavigate();
  const {
    formState: {
      state,
      msaNumber,
      requestingCompanies,
      postcode,
      electricityService,
      isAccountReadOnly,
    },
    setFormState,
    setDisplayingSnackbar,
    setDisplayingAlertBanner,
  } = useContext(FormContext);

  const { values, setFieldValue, setErrors } = useFormikContext<FormValues>();
  const { isHomeUser: isHomeUserFromPermissions } = usePermissions();
  const { setFormLoadValues, homeUser, premiseId } = props;

  const validationSchema = (ces: boolean, readOnly?: boolean) =>
    homeUser
      ? homeUserFormValidationSchema(ces, readOnly, isDigitalEic)
      : formValidationSchema(ces, readOnly, isDigitalEic);

  const hasCes = useMemo(
    () =>
      requestingCompanies.find((c) => c.msaNumber === msaNumber)
        ?.operationalTeam === "CES",
    [requestingCompanies, msaNumber]
  );

  const isVIC = state === "VIC";

  const showMeterProvider = !isVIC && !hasCes;

  const isDigitalEic =
    homeUser ||
    (values.electricityAccountPayer === AccountPayer.SOMEONE_ELSE &&
      values.accountHolderDetailsType === "online");

  const sendEicCommsOnLodge = async () => {
    if (
      isDigitalEic &&
      values.electricityEicStatus !== EICStatusEnum.EIC_ACCEPTED
    ) {
      const { valid, message } = await sendEicComms(
        values.id,
        electricityService?.requestId ?? null,
        "Notification re-sent.",
        "Account failed to save."
      );
      if (valid) {
        setDisplayingAlertBanner(null);
        navigate("/", {
          state: {
            submitted: true,
            isDigitalEic,
            snackbarMessage: message,
          },
        });
      } else {
        setDisplayingSnackbar({
          message,
          tone: valid ? "positive" : "caution",
        });
      }
    }
  };

  const submitRequest = async () => {
    try {
      const submitRequestResponse = await apiClient.put(
        `/submission/${electricityService?.requestId}`
      );
      setFormLoadValues(formDataToFormikMapper(submitRequestResponse.data));

      const responseElectricityService =
        submitRequestResponse.data.services.find(
          (s: Service) => s.service_type === "ELECTRICITY"
        );

      if (
        responseElectricityService.raw_request_status !==
          RequestStatusEnum.DRAFT &&
        responseElectricityService.raw_request_status !==
          RequestStatusEnum.DRAFT_ASSIGNED
      ) {
        setFormState((prev) => ({
          ...prev,
          electricityService: {
            ...prev.electricityService,
            isReadOnly: true,
          },
        }));
      }
      setDisplayingAlertBanner(null);
      navigate("/", {
        state: {
          submitted: true,
          journeyId: responseElectricityService.journey_id,
        },
      });
    } catch (e: any) {
      setDisplayingAlertBanner(getLodgeErrorTypeEnum(e) ?? null);
    }
  };
  const presubmissionRequestUpdate = async (isLodge: boolean) => {
    const manipulatedValues =
      isHomeUserFromPermissions &&
      values.accountHolder === "person" &&
      !accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
      user
        ? {
            ...values,
            accountPrimaryEmail: user.email,
            accountPrimaryFirstName: user.first_name,
            accountPrimaryLastName: user.last_name,
          }
        : values;
    try {
      const updatePremiseResponse = await apiClient.post(
        "/draft/premise",
        premiseFormFieldMapper(
          manipulatedValues,
          {
            electricityService: electricityService ?? {},
          },
          {},
          false,
          undefined,
          isDigitalEic && isLodge,
          homeUser,
          isLodge
        )
      );

      const mappedFormikValues = formDataToFormikMapper(
        updatePremiseResponse.data
      );
      setFormLoadValues({
        ...mappedFormikValues,
        siteSupervisors: values.siteSupervisors,
        licensedPlumbers: values.licensedPlumbers,
      });

      const electricityServiceResponse =
        updatePremiseResponse.data.services.find(
          (s: Service) => s.service_type === "ELECTRICITY"
        );

      setFormState((prevState) => ({
        ...prevState,
        electricityService: {
          draftId: electricityServiceResponse?.draft_id,
          draftVersion: electricityServiceResponse?.draft_version,
          requestId: electricityServiceResponse?.request_id,
        },
      }));
      setDisplayingAlertBanner(null);
    } catch (e) {
      setDisplayingAlertBanner(getLodgeErrorTypeEnum(e));
      throw e;
    }
  };
  //May no longer need as this logic was moved into Yup schema
  useEffect(() => {
    if (getIsNmiRequired(postcode)) {
      setFieldValue("isNmiRequired", true);
    } else {
      setFieldValue("isNmiRequired", false);
    }
  }, [postcode]);

  useEffect(() => {
    setTimeout(() => {
      setIsloading(false);
    }, 2000);
  }, []);

  // cleaning up data from unused form pathways
  useEffect(() => {
    // prevent asp being cleared on form data just loaded
    if (!isLoading) {
      setFieldValue("asp", "");
    }
  }, [values.meterProvider]);

  useEffect(() => {
    if (!values.transformer) {
      setFieldValue("moreThan100Consumption", false);
    }
  }, [values.transformer]);

  useEffect(() => {
    if (!values.truckAppointment) {
      setFieldValue("truckAppointmentReason", "");
    }
  }, [values.truckAppointment]);

  useEffect(() => {
    if (values.locationDescription !== "other") {
      setFieldValue("otherLocationDescription", "");
    }
  }, [values.locationDescription]);

  useEffect(() => {
    if (!values.offpeakRequirements.includes("other")) {
      setFieldValue("otherOffpeakRequirement", "");
    }
  }, [values.offpeakRequirements]);
  //end cleaning up data from unused form pathways

  const saveElectricityBlock = async () => {
    try {
      await presubmissionRequestUpdate(false);
      setDisplayingSnackbar({
        message: "Electricity block updated",
        tone: "positive",
      });
    } catch (error) {
      setDisplayingSnackbar({
        message: "Failed to save electricity block",
        tone: "caution",
      });
    }
  };

  const accountPayers = useMemo(
    () => [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ],
    [
      values.electricityAccountPayer,
      values.gasLine?.accountPayer,
      values.gasMeter?.accountPayer,
      values.both?.accountPayer,
      values.internetAccountPayer,
    ]
  );

  useUpdateSidebarStatus(
    accountPayers,
    Boolean(values.accountId),
    "electricityBlock",
    Boolean(premiseId)
  );

  return (
    <>
      {showUpdatePremiseAlert(
        accountPayers,
        Boolean(values.accountId),
        Boolean(premiseId)
      ) ? (
        <Stack space="xlarge">
          <UpdatePremiseAlert />
        </Stack>
      ) : (
        <Form>
          <Stack
            space={{
              xs: "xlarge",
              md: "xxlarge",
            }}
          >
            {/* Form container */}
            <Stack
              space={{
                xs: "xlarge",
                md: "xxlarge",
              }}
            >
              {homeUser ? (
                <HomeUserLicencedElectrician />
              ) : (
                <LicensedElectrician />
              )}
              {/* Meter Provider / Accredited Service Provider */}
              {showMeterProvider && (
                <MeterProvider hasCes={hasCes} homeUser={homeUser} />
              )}
              {/* Max Load / Transformer Required */}
              <MaxLoad />

              {values.transformer && (
                <FormFieldLayout text="Check with your electrician or engineer to estimate based on site activities, equipment and operating hours.">
                  <Field type="checkbox" name="moreThan100Consumption">
                    {({ field }: { field: FieldInputProps<boolean> }) => (
                      <Checkbox
                        label="Is it likely that this site will consume more than 100MW per annum?"
                        id="moreThan100Consumption"
                        checked={field.value}
                        onChange={(e) => field.onChange(e)}
                      />
                    )}
                  </Field>
                </FormFieldLayout>
              )}

              {isVIC && (
                <FormFieldLayout text="Typically not needed. This is a scheduled on-site meeting with the distributor's crew and an electrician (extra fee, subject to availability).">
                  <Field type="checkbox" name="truckAppointment">
                    {({ field }: { field: FieldInputProps<boolean> }) => (
                      <Checkbox
                        id="truckAppointment"
                        label="Truck appointment"
                        checked={field.value}
                        onChange={(e) => field.onChange(e)}
                      />
                    )}
                  </Field>
                </FormFieldLayout>
              )}

              {values.truckAppointment && (
                <FormFieldLayout>
                  <Field
                    component={GeneralTextField}
                    name="truckAppointmentReason"
                    label="Reason for truck appointment"
                    maxLength={200}
                  />
                </FormFieldLayout>
              )}
              {/* Only show NMI input for NSW */}
              {["NSW"].includes(state) && (
                <FormFieldLayout text="National Meter Identifier and is unique for each premises">
                  <Field component={GeneralTextField} name="nmi" label="NMI" />
                </FormFieldLayout>
              )}
              {/* End NMI input */}
              <FormFieldLayout multipleFields>
                {hasCes && (
                  <Field
                    component={GeneralTextField}
                    name="ntc"
                    label="NTC (Network tariff code)"
                  />
                )}
                <Field
                  component={FormikSelectField}
                  name="installationType"
                  label="Installation type"
                  options={[
                    {
                      label: "Underground",
                      value: "underground",
                    },
                    {
                      label: "Underground to overhead mains",
                      value: "underground_to_overhead_mains",
                    },
                    {
                      label: "Overhead",
                      value: "overhead",
                    },
                    {
                      label: "Overhead to underground mains",
                      value: "overhead_to_underground_mains",
                    },
                    {
                      label: "Transformer ground level",
                      value: "transformer_ground_level",
                    },
                    {
                      label: "Transformer overhead",
                      value: "transformer_overhead",
                    },
                  ]}
                />
              </FormFieldLayout>
              <FormFieldLayout
                text="Required when there's more than one connection at the same address (e.g. shed, public light and power). Not required for units/shops."
                multipleFields
              >
                <Field
                  component={FormikSelectField}
                  name="locationDescription"
                  label="Location description"
                  options={[
                    {
                      label: "Carpark",
                      value: "carpark",
                    },
                    {
                      label: "House lights",
                      value: "house_lights",
                    },
                    {
                      label: "Main switchboard",
                      value: "main_switchboard",
                    },
                    {
                      label: "Public light and power",
                      value: "public_light_and_power",
                    },
                    {
                      label: "Shed",
                      value: "shed",
                    },
                    {
                      label: "Other",
                      value: "other",
                    },
                  ]}
                />
                {values.locationDescription === "other" && (
                  <Field
                    component={GeneralTextField}
                    name="otherLocationDescription"
                    label="Other location description"
                    maxLength={30}
                  />
                )}
              </FormFieldLayout>

              {["NSW", "SA"].includes(state) && (
                <FormFieldLayout
                  text={`No solar on site – no feed-in tariff. If solar is installed after the meter, you'll need to submit a "Change Supply" request.`}
                  multipleFields
                >
                  <Field name="solar">
                    {({ field }: { field: FormikProps<FieldProps> }) => (
                      <RadioGroup
                        {...field}
                        id="solar"
                        name="solar"
                        label="Will there be a solar system on site on the date of meter installation?"
                      >
                        <Box
                          display="flex"
                          flexWrap="wrap"
                          flexDirection={{
                            xs: "column",
                            sm: "row",
                          }}
                        >
                          <Radio id="yes" label="Yes" value="yes" />
                          <Radio id="no" label="No or unsure" value="no" />
                        </Box>
                      </RadioGroup>
                    )}
                  </Field>

                  {values.solar === "yes" && (
                    <Field
                      component={GeneralTextField}
                      name="sizeOfSolarSystem"
                      label="Size of solar system"
                    />
                  )}
                </FormFieldLayout>
              )}

              <FormFieldLayout multipleFields>
                <CheckboxGroup
                  id="offpeakRequirements"
                  label="Off-peak requirements:"
                >
                  <Field
                    type="checkbox"
                    value="hotwater"
                    name="offpeakRequirements"
                  >
                    {({ field }: { field: FieldInputProps<boolean> }) => (
                      <Checkbox
                        {...field}
                        label="Hot water"
                        id="hotwater"
                        value="hotwater"
                      />
                    )}
                  </Field>
                  <Field
                    type="checkbox"
                    value="pool"
                    name="offpeakRequirements"
                  >
                    {({ field }: { field: FieldInputProps<boolean> }) => (
                      <Checkbox
                        {...field}
                        label="Pool heating"
                        id="pool"
                        value="pool"
                      />
                    )}
                  </Field>
                  <Field
                    type="checkbox"
                    value="slab"
                    name="offpeakRequirements"
                  >
                    {({ field }: { field: FieldInputProps<boolean> }) => (
                      <Checkbox
                        {...field}
                        label="Slab heating"
                        id="slab"
                        value="slab"
                      />
                    )}
                  </Field>
                  <Field
                    type="checkbox"
                    value="other"
                    name="offpeakRequirements"
                  >
                    {({ field }: { field: FieldInputProps<boolean> }) => (
                      <Checkbox
                        {...field}
                        label="Other"
                        id="other"
                        value="other"
                      />
                    )}
                  </Field>
                </CheckboxGroup>

                {values.offpeakRequirements.includes("other") && (
                  <Field
                    component={GeneralTextField}
                    name="otherOffpeakRequirement"
                    label="Other off peak requirements"
                    maxLength={30}
                  />
                )}
              </FormFieldLayout>

              {state === "SA" && (
                <FormFieldLayout>
                  <Field
                    component={GeneralTextField}
                    name="rexId"
                    label="REX ID"
                  />
                </FormFieldLayout>
              )}
              {state === "VIC" && (
                <FormFieldLayout
                  text="You can upload more than one CoES if needed"
                  multipleFields
                >
                  <Text>CoES (Certificate of electrical safety)</Text>
                  <Field
                    component={GeneralTextField}
                    name="coes"
                    label="CoES reference number"
                  />
                  <Field name="coesFiles">
                    {({ field }: FieldProps) => (
                      <NewPremiseFormFileUploadFormikField
                        draftId={electricityService?.draftId ?? ""}
                        documentTypes={[
                          DocumentTypeEnum.CERTIFICATE_OF_ELECTRICAL_SAFETY,
                          DocumentTypeEnum.ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY,
                        ]}
                        field={field}
                      />
                    )}
                  </Field>
                </FormFieldLayout>
              )}
              {state === "VIC" && (
                <FormFieldLayout multipleFields>
                  <Text>Mains control CES</Text>
                  <Field name="mcoesFiles">
                    {({ field }: FieldProps) => (
                      <NewPremiseFormFileUploadFormikField
                        draftId={electricityService?.draftId ?? ""}
                        documentTypes={[
                          DocumentTypeEnum.MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY,
                        ]}
                        field={field}
                      />
                    )}
                  </Field>
                </FormFieldLayout>
              )}

              {state === "VIC" && (
                <FormFieldLayout multipleFields>
                  <Text>Electrical Works Request</Text>
                  <Field name="ewrFiles">
                    {({ field }: FieldProps) => (
                      <NewPremiseFormFileUploadFormikField
                        draftId={electricityService?.draftId ?? ""}
                        documentTypes={[
                          DocumentTypeEnum.ELECTRICAL_WORKS_REQUEST,
                        ]}
                        field={field}
                      />
                    )}
                  </Field>
                </FormFieldLayout>
              )}

              <LifeSupportSection
                showPresentation={
                  values.electricityAccountPayer === AccountPayer.COMPANY &&
                  !homeUser &&
                  state === State.VIC
                }
                showWithFormFieldLayout={true}
                fieldProps={{
                  name: "electricityLifeSupport",
                }}
              />
            </Stack>

            {/* Actions */}
            <PermissionsActionButtons
              primaryButtonProps={{
                onHandleClick: async () => {
                  await validationSchema(hasCes, isAccountReadOnly)
                    .validate(values, {
                      context: { meterProvider: values.meterProvider },
                      abortEarly: false,
                    })
                    .then(async () => {
                      await presubmissionRequestUpdate(true);
                    })
                    .then(async () => {
                      if (!isDigitalEic) {
                        await submitRequest();
                      }
                    })
                    .then(async () => {
                      await sendEicCommsOnLodge();
                    })
                    .catch((e) => {
                      if (axios.isAxiosError(e)) return;
                      setErrors(yupToFormErrors(e));
                    });
                },
              }}
              secondaryButtonProps={{
                onHandleClick: saveElectricityBlock,
                disabled: electricityService?.autoSubmit,
              }}
            />
            {/* End Actions */}
          </Stack>
          {/* Form + Button container */}
        </Form>
      )}
    </>
  );
};

export default ElectricityBlock;
